import React, { useState } from 'react';
import _ from 'lodash';
import moment from 'moment';
import { logError } from 'utils/logger';
import { Edit } from '@mui/icons-material';
import { useQuery } from '@apollo/client';
import makeStyles from '@mui/styles/makeStyles';
import { useNotifications } from 'modules/notification';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextareaAutosize } from '@mui/material';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { csNotesApi } from '../api';
import { NotesTable } from './NotesTable';
import { CreditSubmissionNote } from '../types';
import { useCreditSubmission } from '../../hooks/useCreditSubmission';

interface Props {
  csId: string;
  userName: string;
  dynamicCSId: string;
}

export const NotesCard = ({ csId, dynamicCSId, userName }: Props) => {
  const classes = useStyles();

  const { showNotification } = useNotifications();
  const { handleUpdateCreditSubmission } = useCreditSubmission();

  const [notes, setNotes] = useState<CreditSubmissionNote[]>([]);
  const [newNote, setNewNote] = useState<string>('');
  const [openNoteModal, setOpenNoteModal] = useState<boolean>(false);

  useQuery(csNotesApi.getNotes, {
    variables: { creditSubmissionId: csId },
    onCompleted: ({ creditSubmission }) => {
      if (_.isEmpty(creditSubmission.internalNotes)) return;
      setNotes(JSON.parse(creditSubmission.internalNotes));
    },
  });

  const saveCreditSubmission = async (notes: string) => {
    try {
      await handleUpdateCreditSubmission(dynamicCSId, { internalNotes: notes });
      showNotification('Note saves successfully!');
    } catch (err) {
      logError('[updateVendorOpportunity] Issue with save note', err);
      showNotification('Something went wrong!', { type: 'error' });
      (window as any).NREUM.noticeError(err);
    }
  };

  const handleNoteDelete = async (note: CreditSubmissionNote) => {
    const newNotes = [...notes];
    _.remove(newNotes, note);
    await saveCreditSubmission(JSON.stringify(newNotes));
    setNotes(newNotes);
  };

  const handleNoteUpdate = async (original: CreditSubmissionNote, newNote: string) => {
    const newNotes = [...notes];
    const index = _.findIndex(newNotes, original);

    newNotes.splice(index, 1, {
      note: newNote,
      createdOn: original.createdOn,
      createdBy: original.createdBy,
    });

    await saveCreditSubmission(JSON.stringify(newNotes));
    setNotes(newNotes);
  };

  const handleNoteSave = async () => {
    setOpenNoteModal(false);
    const note: CreditSubmissionNote = {
      note: newNote,
      createdBy: userName!,
      createdOn: moment().format(),
    };

    const newNotes = [...notes, note];

    await saveCreditSubmission(JSON.stringify(newNotes));
    setNotes(newNotes);
    setNewNote('');
  };

  return (
    <>
      <CardContainer
        title="Notes"
        actionBtnProps={{
          startIcon: <Edit />,
          label: 'ADD NOTE',
          onClick: () => {
            setOpenNoteModal(true);
          },
        }}
      >
        <NotesTable notes={notes} onNoteDelete={handleNoteDelete} onNoteUpdate={handleNoteUpdate} />
      </CardContainer>

      <Dialog open={openNoteModal} onClose={() => setOpenNoteModal(false)} fullWidth maxWidth="md">
        <DialogTitle>Create New Note</DialogTitle>

        <DialogContent>
          <TextareaAutosize
            placeholder="Enter note..."
            className={classes.notesBox}
            onChange={e => setNewNote(e.target.value)}
          />
        </DialogContent>

        <DialogActions>
          <Button variant="contained" onClick={handleNoteSave}>
            SAVE
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

const useStyles = makeStyles({
  notesBox: {
    width: '100%',
    resize: 'none',
    padding: '5px',
    overflowY: 'auto',
    marginTop: '10px',
    minHeight: '100px',
    maxHeight: '200px',
    borderRadius: '10px',
    border: '1px solid #DDD',
  },
});
